import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { getLoadedSudo, useSudoStale } from '../../utils/sudoState';
import { clearCopilotConversation, readCopilotConversation, writeCopilotConversation } from './copilotStorage';
import { fetchCopilotHistory, startSessionWarmup } from './copilotSession';
import { isNewerHistory, messagesFromHistory, storedMessageOf } from './copilotHistory';
import { screenOf } from './copilotScreen';

// ---------------------------------------------------------------------------
// Copilot conversation state, shared across the whole app.
//
// A conversation belongs to the user, not to a case: Copilot keeps one folder
// per case inside a chat, and every turn says which case (if any) is on screen
// (see `screen`). The trigger shows on every page. A conversation moves
// through three states:
//   - none:   nothing open yet.
//   - draft:  panel opened, nothing sent yet. It survives navigation.
//   - active: at least one message sent. It follows the user to every page,
//             keeps its open/closed state across navigation, and is persisted
//             per tab so a reload restores it (see copilotStorage).
// "New chat" is possible on any page and replaces the current conversation;
// there is no way back to the previous one.
// ---------------------------------------------------------------------------

const CopilotContext = createContext(null);

// The session id is the chat's name in Copilot's storage (a folder per chat), and
// the product API accepts only letters, digits, `_` and `-` up to 100 characters.
const SESSION_ID_MAX_CHARS = 100;

function mintSessionId(userId, workspaceId) {
  return ['session', userId, workspaceId, Date.now()]
    .filter(Boolean)
    .join('_')
    .replace(/[^A-Za-z0-9_-]/g, '')
    .slice(0, SESSION_ID_MAX_CHARS);
}

export function CopilotMinProvider({ children }) {
  const { t } = useTranslation();
  const tRef = useRef(t);
  tRef.current = t;
  const [currentUser] = useCurrentUser();
  const userId = currentUser?.id;
  // The workspace requests actually go to. Under sudo that is the one in
  // localStorage, not the user's own that /users/me reports, and a session is
  // bound to it: a conversation from one workspace must never be restored in
  // another. String on both sides — the sudo id is stored as text.
  const ownWorkspaceId = currentUser?.workspace?.id;
  const workspaceId = ownWorkspaceId == null ? undefined : String(getLoadedSudo().id ?? ownWorkspaceId);
  const featureFlags = currentUser?.workspace?.config?.featureflags;
  const enabled = !!featureFlags?.includes('copilot');
  // Another tab entered or left sudo: every request from this tab now carries
  // that workspace's header (see utils/sudoState.ts) while the page, and any
  // conversation, still belong to the one it loaded under. Switching in this
  // tab is a full reload, so the stored conversation is dropped by the
  // workspace check on hydration instead.
  const sudoStale = useSudoStale();

  // The case page currently mounted (registered via useCopilotMinPage), if any.
  const [page, setPage] = useState(null);
  // What the user is looking at right now, sent with every turn as osavul_context.
  const { pathname: currentPath } = useLocation();
  const screen = useMemo(() => screenOf(currentPath, page), [currentPath, page]);
  // For callbacks that must read the screen at call time (a new draft warms up for the case on screen).
  const screenRef = useRef(screen);
  screenRef.current = screen;
  const [conversation, setConversationState] = useState(null);
  const [isOpen, setIsOpenState] = useState(false);
  const [unread, setUnread] = useState(false);
  // An answer is being written, and the tool it is running: shown on the trigger while the panel is closed.
  const [running, setRunning] = useState({ active: false, label: null });
  const reportRunning = useCallback(
    (next) => setRunning((prev) => (prev.active === next.active && prev.label === next.label ? prev : next)),
    [],
  );
  // Bumped whenever the runtime must start from scratch (new/discarded/hydrated
  // conversation): the chat component remounts its assistant-ui runtime on it.
  const [threadKey, setThreadKey] = useState(0);

  // Refs mirror state for callbacks that run from child effects, which fire
  // before this component's own effects would have synced a ref.
  const conversationRef = useRef(null);
  const isOpenRef = useRef(false);
  const threadRuntimeRef = useRef(null);
  // Session id of a draft that was opened (and warmed up) but then dropped
  // (user closed it, switched workspace): opening again reuses the warm
  // session. Only drafts live here — an active conversation's id must never be
  // reused for an empty chat.
  const draftSessionRef = useRef(null);
  const hydratedRef = useRef(false);
  // Set right before we cancel a run ourselves (Stop, New chat, discard). A
  // run that ends as "cancelled" without it was aborted from outside — the
  // browser tearing the page down on reload — and must stay marked pending.
  const stopRequestedRef = useRef(false);
  // Mode ('qa' | 'report') of the most recent send; persisted with a pending
  // request so a resend goes out the same way.
  const lastTurnModeRef = useRef('qa');
  // True once the page is being unloaded: the snapshot written on `pagehide`
  // is final and must not be overwritten by the stream being torn down.
  const unloadingRef = useRef(false);

  const cancelRun = useCallback(() => {
    stopRequestedRef.current = true;
    threadRuntimeRef.current?.cancelRun();
  }, []);

  const snapshotFor = useCallback(
    (conv, messages, pending) => ({
      sessionId: conv.sessionId,
      userId: conv.userId,
      workspaceId: conv.workspaceId,
      messages,
      pending,
    }),
    [],
  );

  // Reloading (or closing) the tab mid-answer: the backend cancels the turn
  // when the stream drops, so record the request as pending right now, while
  // the page is still alive, instead of relying on how the browser happens to
  // end the fetch. `beforeunload` is the earliest point (nothing is cancelled
  // yet, so the run is still visibly in flight); `pagehide` is the backstop.
  // The app has no beforeunload prompt, so both always lead to an unload;
  // `pageshow` undoes the flag if the page comes back from the bfcache.
  useEffect(() => {
    const onUnload = () => {
      const conv = conversationRef.current;
      const runtime = threadRuntimeRef.current;
      if (conv?.started && runtime && !unloadingRef.current) {
        const state = runtime.getState();
        if (state.isRunning) {
          const messages = state.messages
            .filter((m) => m.role === 'user' || m.role === 'assistant')
            .map(storedMessageOf);
          const lastUser = [...messages].reverse().find((m) => m.role === 'user');
          if (lastUser) {
            writeCopilotConversation(
              snapshotFor(conv, messages, { text: lastUser.content, mode: lastTurnModeRef.current }),
            );
          }
        }
      }
      unloadingRef.current = true;
    };
    const onPageShow = () => {
      unloadingRef.current = false;
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('pagehide', onUnload);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('pagehide', onUnload);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [snapshotFor]);

  const setConversation = useCallback((next) => {
    conversationRef.current = next;
    setConversationState(next);
  }, []);

  const setIsOpen = useCallback((next) => {
    isOpenRef.current = next;
    setIsOpenState(next);
  }, []);

  const resetThread = useCallback(() => setThreadKey((k) => k + 1), []);

  const discardConversation = useCallback(() => {
    cancelRun();
    clearCopilotConversation();
    draftSessionRef.current = null;
    setConversation(null);
    setIsOpen(false);
    setUnread(false);
    resetThread();
  }, [cancelRun, setConversation, setIsOpen, resetThread]);

  // Restore a persisted conversation once the user is known; drop it if it
  // belongs to another user or workspace (login as someone else, workspace
  // switch) or if the user goes away altogether (logout — the provider sits
  // above Layout and outlives it, so nothing may linger in memory or storage).
  // A `pending` turn means the tab reloaded mid-answer: the trailing request
  // (and any partial answer) is taken out of the history and surfaced as
  // "interrupted" so the user can send it again.
  useEffect(() => {
    if (!userId || !workspaceId) {
      if (conversationRef.current) discardConversation();
      return;
    }
    const current = conversationRef.current;
    if (current) {
      if (current.userId !== userId || current.workspaceId !== workspaceId) discardConversation();
      return;
    }
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const stored = readCopilotConversation();
    if (!stored) return;
    if (stored.userId !== userId || stored.workspaceId !== workspaceId) {
      clearCopilotConversation();
      return;
    }
    let messages = stored.messages;
    let interrupted = null;
    if (stored.pending) {
      while (messages.length && messages[messages.length - 1].role === 'assistant') messages = messages.slice(0, -1);
      if (messages.length && messages[messages.length - 1].role === 'user') messages = messages.slice(0, -1);
      interrupted = stored.pending;
    } else if (messages.length && messages[messages.length - 1].role === 'user') {
      // A trailing question with no answer at all means the answer never
      // arrived; offer to resend rather than restore a dangling bubble.
      interrupted = { text: messages[messages.length - 1].content, mode: 'qa' };
      messages = messages.slice(0, -1);
    }
    setConversation({
      sessionId: stored.sessionId,
      userId,
      workspaceId,
      initialMessages: messages,
      started: true,
      interrupted,
    });
    resetThread();

    // The snapshot above is what this tab last rendered. Copilot holds the chat
    // itself, with each answer complete (file links included), so take its copy
    // when it is newer. Not when a turn was interrupted: that one was cancelled.
    if (!interrupted && messages.length) {
      const restoredMessages = messages;
      const { sessionId } = stored;
      fetchCopilotHistory(sessionId).then((history) => {
        const current = conversationRef.current;
        if (!current || current.sessionId !== sessionId || current.initialMessages !== restoredMessages) return;
        // A turn completed (or started) here since the restore: the live thread is ahead of the snapshot.
        const live = threadRuntimeRef.current?.getState();
        if (live?.isRunning || (live?.messages?.length ?? 0) !== restoredMessages.length) return;
        const server = messagesFromHistory(history, tRef.current);
        if (!isNewerHistory(server, restoredMessages)) return;
        setConversation({ ...current, initialMessages: server });
        resetThread();
      });
    }
  }, [userId, workspaceId, discardConversation, setConversation, resetThread]);

  // A conversation must not go on talking to another workspace: stop the run,
  // forget the session and close the panel. The copilot stays hidden until the
  // tab reloads into a workspace of its own.
  useEffect(() => {
    if (sudoStale && conversationRef.current) discardConversation();
  }, [sudoStale, discardConversation]);

  const createDraft = useCallback(
    ({ reuseWarmSession }) => {
      let sessionId = reuseWarmSession ? draftSessionRef.current : null;
      if (!sessionId) {
        sessionId = mintSessionId(userId, workspaceId);
        draftSessionRef.current = sessionId;
        startSessionWarmup(screenRef.current.narrativeId, sessionId);
      }
      setConversation({
        sessionId,
        userId,
        workspaceId,
          initialMessages: [],
        started: false,
        interrupted: null,
      });
      resetThread();
    },
    [userId, workspaceId, setConversation, resetThread],
  );

  const canStartNewChat = !sudoStale;

  const open = useCallback(() => {
    if (!conversationRef.current) {
      if (!canStartNewChat) return;
      createDraft({ reuseWarmSession: true });
    }
    setUnread(false);
    setIsOpen(true);
  }, [canStartNewChat, createDraft, setIsOpen]);

  const close = useCallback(() => setIsOpen(false), [setIsOpen]);

  const startNewChat = useCallback(() => {
    if (!canStartNewChat) return;
    cancelRun();
    clearCopilotConversation();
    createDraft({ reuseWarmSession: false });
    setUnread(false);
    setIsOpen(true);
  }, [canStartNewChat, cancelRun, createDraft, setIsOpen]);

  const registerThreadRuntime = useCallback((runtime) => {
    threadRuntimeRef.current = runtime;
  }, []);

  // Snapshot of the live thread for analytics (the trigger button sits
  // outside the runtime provider and can't use the thread hooks).
  const getThreadSnapshot = useCallback(() => {
    const state = threadRuntimeRef.current?.getState();
    return { messagesCount: state?.messages.length ?? 0, isRunning: state?.isRunning ?? false };
  }, []);

  // Called by the chat whenever its message list or running state changes.
  // The first user message turns a draft into an active conversation; every
  // change after that is written through to storage. `cancelled` says the
  // run that just ended was aborted rather than completed.
  const reportActivity = useCallback(
    ({ messages, isRunning, cancelled }) => {
      if (unloadingRef.current) return;
      const current = conversationRef.current;
      if (!current) return;
      const mode = lastTurnModeRef.current;
      const lastUser = [...messages].reverse().find((m) => m.role === 'user');
      if (!current.started && !lastUser) return;

      // An abort we did not ask for is the page being unloaded mid-answer:
      // keep the request pending so the reloaded tab offers to resend it.
      const interruptedByUnload = cancelled && !stopRequestedRef.current;
      // A new run clears any stale stop request (e.g. Stop pressed with nothing running).
      if (cancelled || isRunning) stopRequestedRef.current = false;

      let next = current;
      if (!next.started) {
        draftSessionRef.current = null;
        next = { ...next, started: true };
      }
      if (next.interrupted && messages.length > next.initialMessages.length) {
        next = { ...next, interrupted: null };
      }
      if (next !== current) setConversation(next);

      let pending = null;
      if ((isRunning || interruptedByUnload) && lastUser) pending = { text: lastUser.content, mode };
      else if (next.interrupted) pending = next.interrupted; // still unresolved after a reload
      writeCopilotConversation(snapshotFor(next, messages, pending));
    },
    [setConversation, snapshotFor],
  );

  const openRef = useRef(open);
  openRef.current = open;

  // An answer landed while the panel was closed (or the user was on another
  // page): mark it unread and say so, with the toast as a shortcut back in.
  const reportTurnFinished = useCallback(({ cancelled }) => {
    if (isOpenRef.current || cancelled) return;
    setUnread(true);
    toast.info(t('Copilot has answered your question'), {
      toastId: 'copilot-answer-ready',
      onClick: () => openRef.current(),
    });
  }, [t]);

  const status = !conversation ? 'none' : conversation.started ? 'active' : 'draft';
  const visible = enabled && !sudoStale;

  const value = useMemo(
    () => ({
      enabled,
      visible,
      status,
      page,
      screen,
      conversation,
      isOpen,
      unread,
      running,
      threadKey,
      canStartNewChat,
      open,
      close,
      startNewChat,
      cancelRun,
      lastTurnModeRef,
      registerThreadRuntime,
      getThreadSnapshot,
      reportActivity,
      reportTurnFinished,
      reportRunning,
      setPage,
    }),
    [
      enabled,
      visible,
      status,
      page,
      screen,
      conversation,
      isOpen,
      unread,
      running,
      threadKey,
      canStartNewChat,
      open,
      close,
      startNewChat,
      cancelRun,
      registerThreadRuntime,
      getThreadSnapshot,
      reportActivity,
      reportTurnFinished,
      reportRunning,
    ],
  );

  return <CopilotContext.Provider value={value}>{children}</CopilotContext.Provider>;
}

export function useCopilotMin() {
  return useContext(CopilotContext);
}

// Case pages call this to tell the copilot which case is on screen and whether
// it qualifies for a chat. Unregistering (unmount or switching case) is what
// closes a draft that was never sent.
export function useCopilotMinPage({ narrativeId, title, eligible }) {
  const copilot = useCopilotMin();
  const setPage = copilot?.setPage;
  const { pathname } = useLocation();

  useEffect(() => {
    if (!setPage || !narrativeId) return undefined;
    setPage({ narrativeId, title: title ?? '', path: pathname, eligible: !!eligible });
    return () => setPage(null);
  }, [setPage, narrativeId, title, pathname, eligible]);
}
